import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { Subject } from 'rxjs';
import { Socket } from 'ngx-socket-io';
import { SocketService } from '../../../_core/services/socket.service';
import { delay, takeUntil, catchError } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { Match } from '../../models/match.model';
import { Set } from '../../models/set.model';
import { Game } from '../../models/game.model';
import { Point, DeviceMeta } from '../../models/point.model';
import { TranslateModule } from '@ngx-translate/core';

// Battery level thresholds
const BATTERY_THRESHOLDS = {
  HIGH: 50,
  LOW: 20
} as const;

// Battery states configuration
const BATTERY_STATES = {
  HIGH: {
    icon: 'battery-full-outline',
    color: 'green'
  },
  MEDIUM: {
    icon: 'battery-half-outline', 
    color: 'orange'
  },
  LOW: {
    icon: 'battery-half-outline',
    color: 'red'
  },
  CRITICAL: {
    icon: 'battery-dead-outline',
    color: 'red'
  }
} as const;

@Component({
  standalone: true,
  selector: 'app-battery-indicator',
  templateUrl: './battery-indicator.component.html',
  styleUrls: ['./battery-indicator.component.scss'],
  imports: [CommonModule, IonicModule, TranslateModule]
})
export class BatteryIndicatorComponent implements OnInit, OnDestroy {
  @Input({ required: true }) match: Match;
  
  batteryPercent: number = 0;
  batteryIcon: string = BATTERY_STATES.HIGH.icon;
  batteryColor: string = BATTERY_STATES.HIGH.color;
  isLoading: boolean = false;
  hasError: boolean = false;
  
  get isMatchInProgress(): boolean {
    return this.match?.matchStatus === 'IN_PROGRESS';
  }
  
  private readonly destroy$: Subject<void> = new Subject();
  private readonly broadcastDelay: number;

  constructor(
    private socket: Socket,
    private socketService: SocketService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.broadcastDelay = Number(this.activatedRoute.snapshot.queryParams.broadcastDelay) || 0;
  }

  ngOnInit() {
    this.updateBatteryFromSets(this.match.deviceMeta);
    this.setupSocketConnection();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSocketConnection(): void {
    try {
      this.socket.emit('join-room', `match/${this.match._id}/sets`);
      
      this.socketService
        .getSocketUpdate('match/sets')
        .pipe(
          takeUntil(this.destroy$),
          delay(this.broadcastDelay * 1000),
          catchError(error => {
            console.error('Socket error in battery indicator:', error);
            this.hasError = true;
            return [];
          })
        )
        .subscribe((value) => {
          const data = value as { _id: string; sets: Set[], deviceMeta: DeviceMeta };
          if (data?._id === this.match._id && Array.isArray(data.sets)) {
            this.match.sets = data.sets;
            this.match.deviceMeta = data.deviceMeta;
            this.updateBatteryFromSets(data.deviceMeta);
          }
        });
    } catch (error) {
      console.error('Failed to setup socket connection:', error);
      this.hasError = true;
    }
  }

  private updateBatteryFromSets(deviceMeta: DeviceMeta): void {
    try {
      if (deviceMeta) {
        this.batteryPercent = deviceMeta.batteryLevel;
        this.setBatteryIconAndColor();
        this.hasError = false;
      }
    } catch (error) {
      console.error('Error updating battery from sets:', error);
      this.hasError = true;
    }
  }  

  private setBatteryIconAndColor(): void {
    if (this.batteryPercent >= BATTERY_THRESHOLDS.HIGH) {
      this.batteryIcon = BATTERY_STATES.HIGH.icon;
      this.batteryColor = BATTERY_STATES.HIGH.color;
    } else if (this.batteryPercent >= BATTERY_THRESHOLDS.LOW) {
      this.batteryIcon = BATTERY_STATES.MEDIUM.icon;
      this.batteryColor = BATTERY_STATES.MEDIUM.color;
    } else {
      this.batteryIcon = BATTERY_STATES.CRITICAL.icon;
      this.batteryColor = BATTERY_STATES.CRITICAL.color;
    }
  }

  // Public method for testing
  getBatteryStatus(): { percent: number; icon: string; color: string } {
    return {
      percent: this.batteryPercent,
      icon: this.batteryIcon,
      color: this.batteryColor
    };
  }

  // Get battery level class for CSS styling
  getBatteryLevelClass(): string {
    if (this.batteryPercent >= BATTERY_THRESHOLDS.HIGH) {
      return 'high';
    } else if (this.batteryPercent >= BATTERY_THRESHOLDS.LOW) {
      return 'medium';
    } else {
      return 'critical';
    }
  }
} 
